import camelCase from 'lodash-es/camelCase'
import mapKeys from 'lodash-es/mapKeys'

import { SECTION_NAMES } from '@constants/sectionNames'
import type { AnalyticsBarChartProps } from '@fourthwall/module-analytics'
import {
  formatMoneyValue,
  formatNumberValue,
  formatPercentageValue,
  formatTopProductsByUnitsSoldCsv,
  getDateFormatter,
} from '@fourthwall/module-analytics'

export const title = SECTION_NAMES.analytics

const renameCsvColumns = (data: any[], newKeys: Record<string, string>) => {
  return data.map((item) => {
    return mapKeys(item, (value, key) =>
      key in newKeys ? camelCase(newKeys[key as keyof typeof newKeys]) : key,
    )
  })
}

const formatOrdersCsv = (tableData: any[]): any[] => {
  const newKeys = {
    averageProfit: 'Average Profit',
    averageSales: 'Average Sales Value',
    cancellations: 'Cancels',
    contribution: 'Total',
    contributionDonations: 'Donations',
    contributionGiftsOnCreatorCost: 'Giveaway Links',
    contributionGiveaway: 'Twitch Gifts',
    contributionOrders: 'Shop Orders',
    contributionSubscriptions: 'Memberships',
    cost: 'Costs',
  }

  return renameCsvColumns(tableData, newKeys)
}

const formatCsvBarChartData = (
  data: AnalyticsBarChartProps['data'],
  newKeys: Record<string, string>,
) => {
  return renameCsvColumns(data, newKeys)
}

export const REPORT_TYPES = {
  profit: {
    slug: 'profit',
    title: 'Profit',
    precision: true,
    externalSource: undefined,
    analyticsChartQuery: 'profit',
    chartValueQuery: undefined,
    endpoint: 'chart',
    getDateFormatter,
    formatValue: formatMoneyValue,
    formatCsv: undefined,
  },
  totalProfit: {
    slug: 'profit',
    title: 'Total profit',
    precision: true,
    externalSource: undefined,
    analyticsChartQuery: 'profit',
    chartValueQuery: undefined,
    endpoint: 'chart',
    getDateFormatter,
    formatValue: formatMoneyValue,
    formatCsv: undefined,
  },
  shopProfit: {
    slug: 'profit',
    title: 'Shop profit',
    precision: true,
    externalSource: undefined,
    analyticsChartQuery: 'profit',
    chartValueQuery: undefined,
    endpoint: 'chart',
    getDateFormatter,
    formatValue: formatMoneyValue,
    formatCsv: undefined,
  },
  membershipsProfit: {
    slug: 'profit',
    title: 'Memberships profit',
    precision: true,
    externalSource: undefined,
    analyticsChartQuery: 'profit',
    chartValueQuery: undefined,
    endpoint: 'chart',
    getDateFormatter,
    formatValue: formatMoneyValue,
    formatCsv: undefined,
  },
  'memberships-total-members': {
    slug: 'memberships-total-members',
    title: 'Active members',
    precision: true,
    analyticsChartQuery: 'totalMembers',
    externalSource: undefined,
    chartValueQuery: undefined,
    endpoint: 'membership-chart',
    getDateFormatter,
    formatValue: formatNumberValue,
    formatCsv: undefined,
  },
  'memberships-total-free-members': {
    slug: 'memberships-total-free-members',
    title: 'Free accounts',
    tooltip: 'Includes free account signups and former members',
    precision: true,
    analyticsChartQuery: 'totalFreeAccounts',
    externalSource: undefined,
    chartValueQuery: undefined,
    endpoint: 'membership-chart',
    getDateFormatter,
    formatValue: formatNumberValue,
    formatCsv: undefined,
  },
  'memberships-members-change': {
    slug: 'memberships-members-change',
    title: 'Members change',
    precision: true,
    analyticsChartQuery: 'membersChange',
    externalSource: undefined,
    chartValueQuery: undefined,
    endpoint: 'membership-chart',
    getDateFormatter,
    formatValue: formatNumberValue,
    formatCsv: undefined,
  },
  'memberships-free-members-change': {
    slug: 'memberships-free-members-change',
    title: 'Free accounts change',
    precision: true,
    analyticsChartQuery: 'freeAccountsChange',
    externalSource: undefined,
    chartValueQuery: undefined,
    endpoint: 'membership-chart',
    getDateFormatter,
    formatValue: formatNumberValue,
    formatCsv: undefined,
  },
  orders: {
    slug: 'orders',
    title: 'Orders',
    precision: true,
    analyticsChartQuery: 'contribution',
    externalSource: undefined,
    chartValueQuery: undefined,
    endpoint: 'chart',
    getDateFormatter,
    formatValue: formatNumberValue,
    formatCsv: formatOrdersCsv,
  },
  shopOrders: {
    slug: 'orders',
    title: 'Shop orders',
    precision: true,
    analyticsChartQuery: 'contribution',
    externalSource: undefined,
    chartValueQuery: undefined,
    endpoint: 'chart',
    getDateFormatter,
    formatValue: formatNumberValue,
    formatCsv: formatOrdersCsv,
  },
  membershipsTransactions: {
    slug: 'orders',
    title: 'Membership transactions',
    precision: true,
    analyticsChartQuery: 'contribution',
    externalSource: undefined,
    chartValueQuery: undefined,
    endpoint: 'chart',
    getDateFormatter,
    formatValue: formatNumberValue,
    formatCsv: formatOrdersCsv,
  },
  'average-order-value': {
    slug: 'average-order-value',
    title: 'Average shop order value',
    precision: true,
    externalSource: undefined,
    analyticsChartQuery: 'contributionAvg',
    chartValueQuery: 'averageGrossSalesValue',
    endpoint: 'chart',
    getDateFormatter,
    formatValue: formatMoneyValue,
    formatCsv: formatOrdersCsv,
  },
  'top-products-by-units-sold': {
    slug: 'top-products-by-units-sold',
    title: 'Top products by units sold',
    precision: false,
    externalSource: undefined,
    analyticsChartQuery: undefined,
    chartValueQuery: undefined,
    endpoint: 'top-offer',
    getDateFormatter,
    formatValue: undefined,
    formatCsv: formatTopProductsByUnitsSoldCsv,
  },
  'sales-by-country': {
    slug: 'sales-by-country',
    title: 'Sales by country',
    precision: false,
    externalSource: undefined,
    analyticsChartQuery: undefined,
    chartValueQuery: undefined,
    endpoint: 'top-sale-by-country',
    getDateFormatter,
    formatValue: undefined,
    formatCsv: undefined,
  },
  'membership-top-tiers': {
    slug: 'membership-top-tiers',
    title: 'Top tiers by members',
    precision: false,
    externalSource: undefined,
    analyticsChartQuery: undefined,
    chartValueQuery: undefined,
    endpoint: 'membership-top-tiers',
    getDateFormatter,
    formatValue: undefined,
    formatCsv: undefined,
  },
  'payment-method-used': {
    slug: 'payment-method-used',
    title: 'Payment method used',
    analyticsChartQuery: undefined,
    chartValueQuery: undefined,
    externalSource: undefined,
    precision: false,
    endpoint: 'payment-method-used',
    formatCsv: (data: AnalyticsBarChartProps['data']) => {
      return formatCsvBarChartData(data, { label: 'Payment method', value: 'Percentage' })
    },
    getDateFormatter,
    formatValue: undefined,
  },
  'conversion-rates': {
    slug: 'conversion-rates',
    title: 'Conversion rate',
    externalSource: 'google-analytics',
    analyticsChartQuery: 'conversion-rates',
    chartValueQuery: undefined,
    precision: false,
    endpoint: 'shops-stats-traffic',
    formatCsv: undefined,
    getDateFormatter,
    formatValue: formatPercentageValue,
  },
  'unique-visitors': {
    slug: 'unique-visitors',
    title: 'Visitors',
    externalSource: 'google-analytics',
    analyticsChartQuery: 'unique-visitors',
    chartValueQuery: undefined,
    precision: false,
    endpoint: 'shops-stats-traffic',
    formatCsv: undefined,
    getDateFormatter,
    formatValue: formatNumberValue,
  },
  'sales-by-traffic-source': {
    slug: 'sales-by-traffic-source',
    title: 'Sales by traffic source',
    externalSource: 'google-analytics',
    analyticsChartQuery: undefined,
    chartValueQuery: undefined,
    precision: false,
    endpoint: 'shops-stats-traffic',
    formatCsv: (data: AnalyticsBarChartProps['data']) => {
      return formatCsvBarChartData(data, { label: 'Traffic source', value: 'Sale value' })
    },
    getDateFormatter,
    formatValue: undefined,
  },
  'sessions-by-traffic-source': {
    slug: 'sessions-by-traffic-source',
    title: 'Sessions by traffic source',
    externalSource: 'google-analytics',
    precision: false,
    analyticsChartQuery: undefined,
    chartValueQuery: undefined,
    endpoint: 'shops-stats-traffic',
    formatCsv: (data: AnalyticsBarChartProps['data']) => {
      return formatCsvBarChartData(data, { label: 'Traffic source', value: 'Sessions' })
    },
    getDateFormatter,
    formatValue: undefined,
  },
  'sales-by-social-source': {
    slug: 'sales-by-social-source',
    title: 'Sales by social source',
    externalSource: 'google-analytics',
    precision: false,
    analyticsChartQuery: undefined,
    chartValueQuery: undefined,
    endpoint: 'shops-stats-traffic',
    formatCsv: (data: AnalyticsBarChartProps['data']) => {
      return formatCsvBarChartData(data, { label: 'Social source', value: 'Sale value' })
    },
    getDateFormatter,
    formatValue: undefined,
  },
  'sessions-by-social-source': {
    slug: 'sessions-by-social-source',
    title: 'Sessions by social source',
    externalSource: 'google-analytics',
    precision: false,
    analyticsChartQuery: undefined,
    chartValueQuery: undefined,
    endpoint: 'shops-stats-traffic',
    formatCsv: (data: AnalyticsBarChartProps['data']) => {
      return formatCsvBarChartData(data, { label: 'Social source', value: 'Sessions' })
    },
    getDateFormatter,
    formatValue: undefined,
  },
} as const
